import { useParams } from 'react-router-dom'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import type { PlanMetadataUpdateDTO } from '@api/fastAPI.schemas'
import { useUpdatePlanMetadata as useUpdatePlanMetadataMutation } from '@api/plan-metadata/plan-metadata'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import { getPlans } from '@queries'

/** Applies a metadata update to a cached plan (omitted fields stay unchanged). */
const applyMetadata = (plan: Plan, { name, comment, tags }: PlanMetadataUpdateDTO): Plan => ({
  ...plan,
  ...(name != null && { node_name: name }),
  ...(comment != null && { comment }),
  ...(tags != null && { tags }),
})

/** Shared by every row's/form's instance, so a save knows about the others still running. */
const UPDATE_PLAN_METADATA_MUTATION_KEY = ['updatePlanMetadata']

/**
 * Changes a saved plan's name, comment and/or tags (the PDF is left untouched). The cached plan
 * list is updated optimistically; a failed request refetches it, so the cache (and everything
 * rendered from it) falls back to what the server has.
 */
const useUpdatePlanMetadata = () => {
  const { projectId } = useParams()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const plansKey = getPlans.getKey(projectId as string)

  const { mutate, isLoading } = useUpdatePlanMetadataMutation<AxiosError>({
    mutation: {
      mutationKey: UPDATE_PLAN_METADATA_MUTATION_KEY,
      onMutate: async ({ planGuid, data }) => {
        await queryClient.cancelQueries(plansKey)
        queryClient.setQueryData<Plan[]>(plansKey, plans =>
          plans?.map(plan => (plan.guid === planGuid ? applyMetadata(plan, data) : plan)),
        )
      },
      onSuccess: () => {
        // A refetch while another metadata save is still running would answer with its old values
        // and briefly revert that save's optimistic ones; the last save to finish refetches.
        if (queryClient.isMutating({ mutationKey: UPDATE_PLAN_METADATA_MUTATION_KEY }) <= 1) {
          queryClient.invalidateQueries(plansKey)
        }
      },
      onError: error => {
        // Refetch rather than restoring a snapshot: with overlapping saves the snapshot may already
        // contain another save's optimistic values, while the server's answer is always the truth.
        queryClient.invalidateQueries(plansKey)
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Speichern des Plans'), {
          variant: 'error',
        })
      },
    },
  })

  const updatePlanMetadata = (plan: Plan, data: PlanMetadataUpdateDTO) =>
    mutate({ projectGuid: projectId as string, planGuid: plan.guid, data })

  return { updatePlanMetadata, isUpdating: isLoading }
}

export default useUpdatePlanMetadata
